import { combine, createEffect, createEvent, createStore, sample } from "effector";
import { appMounted } from "@features/app/model";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IRoadmapsContract } from "@features/roadmaps/model";
import { IAmplitudeFlagsContract } from "@features/amplitude-flags/model";
import { IRouterContract } from "@features/shared/router";
import { ICertificateModalContract } from "@features/certificate/model";
import { IRoadmap, IRoadmapCourse } from "@features/shared/entities/roadmaps";
import {
    roadmapCertificateClicked,
    roadmapCertificateLockedClicked,
    roadmapCertificateUnlockCtaClicked,
    roadmapCourseClicked,
    roadmapHeroClicked
} from "@features/shared/events";

const flattenCourses = (roadmap: IRoadmap | null): IRoadmapCourse[] => {
    if (!roadmap) return [];
    return roadmap.steps.flatMap((s) => s.courses);
};

const sourceType = (course: IRoadmapCourse): "course" | "challenge" =>
    course.type === "course" ? "course" : "challenge";

const courseUrl = (course: IRoadmapCourse): string =>
    course.type === "course" || course.tags?.includes("challenge") ? `/course/${course.id}` : `/challenge/${course.id}`;

const totalTopics = (roadmap: IRoadmap): number =>
    roadmap.steps.reduce((sum, step) => sum + step.courses.reduce((acc, c) => acc + (c.totalTopics ?? 0), 0), 0);

// Passed through the certificate URL as a bare number: the image/PDF route
// renders it with the localized "topics" unit, so switching the certificate
// language re-labels the count instead of baking English into the URL.
const topicsCount = (count: number): string => String(count);

const continueClicked = createEvent();
const courseClicked = createEvent<IRoadmapCourse>();
const certificateClicked = createEvent();
// Locked tile tapped: the "Your certificate" section scrolls into view and highlights itself.
const lockedCertificateClicked = createEvent();
// "Learn to unlock" in that section: same destination as the hero's continue button.
const unlockCtaClicked = createEvent();

const $roadmap = createStore<IRoadmap | null>(null);
const $featureEnabled = createStore<boolean | null>(null);
const $courses = $roadmap.map(flattenCourses);
const $progress = $roadmap.map((r) => Math.max(0, Math.min(100, Math.floor(r?.progress ?? 0))));
const $isComplete = $progress.map((p) => p >= 100);
const $isVisible = combine(
    $featureEnabled,
    $roadmap,
    $courses,
    (enabled, roadmap, courses) => enabled === true && !!roadmap && courses.length > 0
);
const $completedCoursesCount = $courses.map(
    (courses) => courses.filter((course) => (course.enrollmentProgress ?? 0) >= 100).length
);
// Monotonic "scroll me" ticket: every locked tap bumps it, the section reacts to each new value.
const $certificateInfoRequestId = createStore(0).on(lockedCertificateClicked, (n) => n + 1);

const trackContinueClickedFx = createEffect((course: IRoadmapCourse) => {
    roadmapHeroClicked(course.id, sourceType(course));
});
const trackCourseClickedFx = createEffect((course: IRoadmapCourse) => {
    roadmapCourseClicked(course.id, sourceType(course));
});
const trackCertificateClickedFx = createEffect((roadmapId: number) => {
    roadmapCertificateClicked(roadmapId);
});
const trackLockedCertificateClickedFx = createEffect(
    ({ roadmapId, progress }: { roadmapId: number; progress: number }) => {
        roadmapCertificateLockedClicked(roadmapId, progress);
    }
);
const trackUnlockCtaClickedFx = createEffect((roadmapId: number) => {
    roadmapCertificateUnlockCtaClicked(roadmapId);
});

const contract = new ModelContract(
    { $roadmap, $courses, $progress, $isComplete, $isVisible, $completedCoursesCount, $certificateInfoRequestId },
    { continueClicked, courseClicked, certificateClicked, lockedCertificateClicked, unlockCtaClicked },
    {},
    {}
);

export type IRoadmapCertificateProgramContract = typeof contract;

export class RoadmapCertificateProgramModule extends ModuleDescription<IRoadmapCertificateProgramContract> {
    constructor(
        public roadmapsModule: IRoadmapsContract,
        public amplitudeFlagsModule: IAmplitudeFlagsContract,
        public routerModule: IRouterContract,
        public certificateModalModule: ICertificateModalContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({ clock: this.roadmapsModule.stores.$selectedRoadmap, target: $roadmap });
        // Mirrors the flag on every change and once on mount (a flag already resolved never "updates").
        sample({
            clock: [this.amplitudeFlagsModule.stores.$roadmapCertificateEnabled, appMounted],
            source: this.amplitudeFlagsModule.stores.$roadmapCertificateEnabled,
            target: $featureEnabled
        });

        sample({ clock: courseClicked, target: trackCourseClickedFx });
        sample({ clock: courseClicked, fn: courseUrl, target: this.routerModule.events.routerPush });

        sample({
            clock: continueClicked,
            source: this.roadmapsModule.stores.$activeRoadmapCourse,
            filter: (course): course is IRoadmapCourse => Boolean(course),
            target: trackContinueClickedFx
        });
        sample({
            clock: continueClicked,
            source: this.roadmapsModule.stores.$activeRoadmapCourse,
            filter: (course): course is IRoadmapCourse => Boolean(course),
            fn: courseUrl,
            target: this.routerModule.events.routerPush
        });

        sample({
            clock: lockedCertificateClicked,
            source: { roadmap: $roadmap, progress: $progress },
            filter: ({ roadmap }) => roadmap !== null,
            fn: ({ roadmap, progress }) => ({ roadmapId: roadmap!.id, progress }),
            target: trackLockedCertificateClickedFx
        });

        sample({
            clock: unlockCtaClicked,
            source: $roadmap,
            filter: (roadmap) => roadmap !== null,
            fn: (roadmap) => roadmap!.id,
            target: trackUnlockCtaClickedFx
        });
        sample({ clock: unlockCtaClicked, target: continueClicked });

        sample({
            clock: certificateClicked,
            source: this.roadmapsModule.stores.$selectedRoadmap,
            filter: (roadmap): roadmap is IRoadmap => Boolean(roadmap),
            fn: (roadmap) => roadmap.id,
            target: trackCertificateClickedFx
        });
        sample({
            clock: certificateClicked,
            source: this.roadmapsModule.stores.$selectedRoadmap,
            filter: (roadmap): roadmap is IRoadmap => Boolean(roadmap),
            fn: (roadmap) => ({
                open: true,
                roadmapId: roadmap.id,
                roadmapName: roadmap.name,
                roadmapCourseLength: topicsCount(totalTopics(roadmap))
            }),
            target: this.certificateModalModule.events.shareCertificateSheetOpenChanged
        });
    }
}
